import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {TableColumn, TableConfiguration} from "../../../tandem-core/models/table-configuration";
import {Liability} from "../../models/liability";
import {CurrencyService} from "../../../tandem-core/services/currency.service";
import {LiabilityCategory} from "../../models/liability-category";
import {DialogService} from "../../../tandem-core/services/dialog.service";
import {Overlay} from "@angular/cdk/overlay";
import {ModalRef} from "../../../tandem-core/models/modal-ref";
import {LiabilityDialogComponent} from "../liability-dialog/liability-dialog.component";
import {PercentPipe} from "@angular/common";
import {Timestamp} from "firebase/firestore";
import {Chat, CommentPanelComponent} from "../../../tandem-core/components/chat/comment-panel/comment-panel.component";
import {AuthService} from "../../../auth/services/auth.service";
import {Subscription} from "rxjs";
import {LiabilityDetails} from "../../models/financial-position-template";

@Component({
  selector: 'tandem-liability-table',
  templateUrl: './liability-table.component.html',
  styleUrls: ['./liability-table.component.scss']
})
export class LiabilityTableComponent implements OnInit, OnChanges, OnDestroy {

  @Input() liabilityCategory: LiabilityCategory = {name: 'Liabilities', liabilities: [], hoverHint: ''};
  @Input() mode: 'create' | 'edit' | 'view' | 'proposeChanges' = 'create';

  @Input() details?: LiabilityDetails;

  tableConfig: TableConfiguration<Liability> | undefined;
  tableData: Liability[] = [];

  userId: string | null = null;
  private eventSubscription?: Subscription;

  @Output() changesMade: EventEmitter<void> = new EventEmitter<void>();
  @Output() onComment: EventEmitter<{ liabilityCategory: LiabilityCategory, liability: Liability, comment: Chat }> = new EventEmitter<{liabilityCategory: LiabilityCategory; liability: Liability, comment: Chat}>();

  constructor(private currencyService: CurrencyService, private auth: AuthService, private overlay: Overlay, private cdr: ChangeDetectorRef,
              private percentPipe: PercentPipe,
              private dialogService: DialogService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.tableConfig) {
      this.tableConfig.columns = this.getColumns();
      this.tableConfig.addFunction = undefined;
    }
    if (changes['mode'].currentValue !== 'view' && this.tableConfig) {
      this.tableConfig.addFunction =  () => this.openLiabilityDialog();
      this.tableConfig.collapsed = false;
    }
  }

  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.auth.$user.subscribe(user => {
      this.userId = user?.uid || null;
    })

    this.tableConfig = {
      addFunction: () => this.openLiabilityDialog(),
      canBeHidden: false,
      columns: this.getColumns(),
      deleteRowFunction: (liability: Liability) => this.confirmDelete(liability),
      editRowFunction: (liability: Liability) => this.openLiabilityDialog(liability),
      onRowClick: (liability: Liability) => this.openLiabilityDialog(liability),
      noDataMessage: `No ${this.liabilityCategory.name} Liabilities`,
      // onRowClick(t: Liability): void {
      // },
      subtitle: this.liabilityCategory.name,
      hoverHint: this.liabilityCategory.hoverHint,
      totalProp: 'value',
      draggable: ['create', 'edit'].includes(this.mode),
      collapsible: true,
      collapsed: false
    };
    this.tableData = this.liabilityCategory.liabilities;
    if (this.mode === 'view') {
      this.tableConfig.addFunction = undefined;
      this.tableConfig.editRowFunction = undefined;
      this.tableConfig.deleteRowFunction = undefined;
      this.tableConfig.commentRowFunction = (row) => this.openComments(row)
    }

    this.eventSubscription = this.dialogService.componentEvents$.subscribe(
      event => this.handleChatEvent(event)
    );
  }

  public openLiabilityDialog(inputLiability?: Liability): void {
    let title = '';
    if (!inputLiability) {
      title = `Add ${this.liabilityCategory.name}`;
    } else {
      switch (this.mode) {
        case "create":
          title = `Edit ${this.liabilityCategory.name}`;
          break;
        case "edit":
          title = `Edit ${this.liabilityCategory.name}`;
          break;
        case "view":
          title = `View ${this.liabilityCategory.name}`;
          break;
        case "proposeChanges":
          title = 'Propose Changes';
          break;
      }
    }
    const ref: ModalRef = this.dialogService.openModal(LiabilityDialogComponent, {inputLiability: inputLiability, title: title, mode: this.mode, liabilityDetails: this.details});

    ref.afterClosed().subscribe(liability => {
      if (liability) {
        if (inputLiability) {
          let i = this.liabilityCategory.liabilities.indexOf(inputLiability);
          this.liabilityCategory.liabilities[i] = liability;
        } else {
          this.liabilityCategory.liabilities.push(liability);
        }
        this.tableData = [...this.liabilityCategory.liabilities];
        this.cdr.detectChanges();
        this.changesMade.emit();
      }
    })
  }

  public confirmDelete(liability: Liability) {
    this.dialogService.openConfirmDialog('Delete Liability', `Are you sure you want to delete ${liability.name}?`)
      .afterClosed().subscribe(confirmed => {
      if(confirmed) {
        let index: number = this.liabilityCategory.liabilities.indexOf(liability);
        this.liabilityCategory.liabilities.splice(index, 1);
        this.liabilityCategory.liabilities = [...this.liabilityCategory.liabilities]
        this.tableData = [...this.liabilityCategory.liabilities]
        this.cdr.detectChanges();
        this.changesMade.emit();
      }
    })
  }

  private getColumns(): TableColumn<Liability>[] {
    if (this.mode === 'proposeChanges') {
      return [
        {
          title: this.details?.alternateNameLabel || 'Name',
          itemProperty: 'name',
          sortable: true,
          columnType: 'text'
        },
        {
          title: `Starting ${this.details?.alternateValueLabel || 'Value'}`,
          itemProperty: 'value',
          sortable: true,
          displayMask: (t: Liability) => this.currencyService.formatNumber(t.value) as string,
          columnType: 'value'
        },
        {
          title: this.details?.alternateInterestRateLabel || 'Interest Rate',
          itemProperty: 'interestRate',
          sortable: true,
          displayMask: (t: Liability) =>this.percentPipe.transform(t.interestRate) as string,
          columnType: 'text'
        },
        {
          title: 'Proposed Change',
          itemProperty: 'value',
          sortable: true,
          displayMask: (t: Liability) => {
            let total = 0;
            t.proposedChanges?.forEach(pc => {
              if (pc.sourceCategory === this.liabilityCategory.name && pc.sourceItem === t.name) {
                total += pc.amount;
              } else if (pc.destinationCategory === this.liabilityCategory.name && pc.destinationItem === t.name) {
                total -= pc.amount;
              }
            })
            return this.currencyService.formatNumber(total) as string
            // return this.currencyService.formatNumber(0);
          },
          columnType: 'value'
        },
        {
          title: `Ending ${this.details?.alternateValueLabel || 'Value'}`,
          itemProperty: 'value',
          sortable: true,
          displayMask: (t: Liability) => {
            let total = 0;
            t.proposedChanges?.forEach(pc => {
              if (pc.sourceCategory === this.liabilityCategory.name && pc.sourceItem === t.name) {
                total += pc.amount;
              } else if (pc.destinationCategory === this.liabilityCategory.name && pc.destinationItem === t.name) {
                total -= pc.amount;
              }
            })
            return this.currencyService.formatNumber(total + t.value) as string
            // return this.currencyService.formatNumber(0);
          },
          columnType: 'value'
        },
        // {
        //   title: 'ANNUAL RETURN',
        //   itemProperty: 'annualPercentageReturn',
        //   sortable: true,
        //   displayMask: (t: Asset) => this.currencyService.formatPercent(t.annualPercentageReturn),
        //   columnType: 'text'
        // },
      ];
    }
    return [
      {
        title: this.details?.alternateNameLabel || 'Name',
        itemProperty: 'name',
        columnType: 'text',
        sortable: true
      },
      {
        title: this.details?.alternateValueLabel || 'Value',
        itemProperty: 'value',
        columnType: 'value',
        sortable: true,
        displayMask: (t: Liability) => {return this.currencyService.formatNumber(t.value)}
      },
      {
        title: this.details?.alternateInterestRateLabel || 'Interest Rate',
        itemProperty: 'interestRate',
        columnType: 'text',
        sortable: true,
        displayMask: (t: Liability) => {return this.currencyService.formatPercent(t.interestRate)}
      }
    ];
  }

  openComments(liability: Liability) {
    const chatData = {
      chats: liability.comments ? liability.comments : [],
      chatType: 'liability',
      liability: liability,
      liabilityCategory: this.liabilityCategory,
    };
    this.dialogService.openSidePanel(CommentPanelComponent, chatData);
  }

  private handleChatEvent(event: any) {
    if (event.type === 'liabilityMessage') {
      const chat: Chat = {
        message: event.data,
        date: Timestamp.now(),
        status: 'Delivered',
        userId: this.userId || ''
      };


      this.onComment.emit({
        liability: event.liability,
        comment: chat,
        liabilityCategory: this.liabilityCategory
      });
    }
  }
}
